import React, { useState, useEffect, useCallback, memo } from "react";
import { X, ChevronRight, ChevronLeft, Check, MessageCircle, Phone, Loader2, CheckCircle2, AlertCircle, Send, User, Mail, Smartphone, Briefcase, BarChart3 } from "lucide-react";
import api from "../services/api";

/* ─── SOUS-COMPOSANTS SORTIS DU COMPOSANT PRINCIPAL ─── */

const StepInfos = memo(({ formData, onChange, errors }) => (
  <div className="space-y-4">
    <div className="text-center mb-6">
      <div className="w-16 h-16 mx-auto bg-gradient-to-br from-emerald-500/20 to-indigo-500/20 rounded-2xl flex items-center justify-center mb-4 border border-emerald-500/20">
        <User size={28} className="text-emerald-400" />
      </div>
      <h3 className="text-xl font-black text-white mb-2" style={{ fontFamily: "'Outfit', sans-serif" }}>
        Commençons par vos coordonnées
      </h3>
      <p className="text-slate-400 text-sm">Un expert vous contactera après cette courte inscription.</p>
    </div>

    <div className="grid grid-cols-2 gap-3">
      <div>
        <label className="block text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-1.5">Prénom *</label>
        <div className="relative">
          <User size={14} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-500" />
          <input
            type="text"
            value={formData.prenom}
            onChange={(e) => onChange("prenom", e.target.value)}
            placeholder="Votre prénom"
            className="w-full pl-9 pr-4 py-3 bg-slate-950/50 border border-white/10 rounded-xl text-white text-sm placeholder-slate-600 focus:border-emerald-500/50 focus:outline-none focus:ring-1 focus:ring-emerald-500/20 transition-all"
          />
        </div>
      </div>
      <div>
        <label className="block text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-1.5">Nom *</label>
        <div className="relative">
          <User size={14} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-500" />
          <input
            type="text"
            value={formData.nom}
            onChange={(e) => onChange("nom", e.target.value)}
            placeholder="Votre nom"
            className="w-full pl-9 pr-4 py-3 bg-slate-950/50 border border-white/10 rounded-xl text-white text-sm placeholder-slate-600 focus:border-emerald-500/50 focus:outline-none focus:ring-1 focus:ring-emerald-500/20 transition-all"
          />
        </div>
      </div>
    </div>

    <div>
      <label className="block text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-1.5">Téléphone *</label>
      <div className="relative">
        <Smartphone size={14} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-500" />
        <input
          type="tel"
          value={formData.telephone}
          onChange={(e) => onChange("telephone", e.target.value)}
          placeholder="+33 6 12 34 56 78"
          className="w-full pl-9 pr-4 py-3 bg-slate-950/50 border border-white/10 rounded-xl text-white text-sm placeholder-slate-600 focus:border-emerald-500/50 focus:outline-none focus:ring-1 focus:ring-emerald-500/20 transition-all"
        />
      </div>
    </div>

    <div>
      <label className="block text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-1.5">Email *</label>
      <div className="relative">
        <Mail size={14} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-500" />
        <input
          type="email"
          value={formData.email}
          onChange={(e) => onChange("email", e.target.value)}
          placeholder="email@exemple.com"
          className="w-full pl-9 pr-4 py-3 bg-slate-950/50 border border-white/10 rounded-xl text-white text-sm placeholder-slate-600 focus:border-emerald-500/50 focus:outline-none focus:ring-1 focus:ring-emerald-500/20 transition-all"
        />
      </div>
    </div>
  </div>
));

const SingleQuestion = memo(({ question, formData, onAnswer }) => {
  const answer = formData.responses?.[question.question_key] || formData[question.question_key] || "";

  const handleAnswer = (value) => {
    onAnswer(question.question_key, value);
  };

  const isSelect = question.question_type === "select";
  const isRadio = question.question_type === "radio";
  const isTextarea = question.question_type === "textarea";
  const isInput = question.question_type === "text" || !question.question_type;

  const options = typeof question.options === "string" 
    ? JSON.parse(question.options) 
    : Array.isArray(question.options) ? question.options : [];

  const iconMap = {
    "trading_experience": BarChart3,
    "experience_description": Briefcase,
    "domaine_travail": Briefcase,
  };
  const Icon = iconMap[question.question_key] || BarChart3;

  return (
    <div className="space-y-4">
      <div className="text-center mb-4">
        <div className="w-16 h-16 mx-auto bg-gradient-to-br from-emerald-500/20 to-indigo-500/20 rounded-2xl flex items-center justify-center mb-4 border border-emerald-500/20">
          <Icon size={28} className="text-emerald-400" />
        </div>
        <h3 className="text-xl font-black text-white mb-2" style={{ fontFamily: "'Outfit', sans-serif" }}>
          {question.question_text}
        </h3>
        {question.is_required && (
          <span className="text-[10px] font-bold uppercase tracking-widest text-rose-400">* Requis</span>
        )}
      </div>

      {(isSelect || isRadio) && (
        <div className="space-y-2 max-h-64 overflow-y-auto pr-1">
          {options.map((opt) => (
            <button
              key={opt}
              type="button"
              onClick={() => handleAnswer(opt)}
              className={`w-full text-left px-4 py-3 rounded-xl border text-sm font-medium transition-all flex items-center gap-3 ${
                answer === opt
                  ? "bg-emerald-500/10 border-emerald-500/40 text-emerald-300"
                  : "bg-slate-950/30 border-white/5 text-slate-400 hover:border-white/20"
              }`}
            >
              <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                answer === opt ? "border-emerald-500 bg-emerald-500/20" : "border-slate-600"
              }`}>
                {answer === opt && <div className="w-2.5 h-2.5 rounded-full bg-emerald-400" />}
              </div>
              {opt}
            </button>
          ))}
        </div>
      )}

      {isTextarea && (
        <textarea
          value={answer}
          onChange={(e) => handleAnswer(e.target.value)}
          placeholder="Décrivez votre expérience..."
          rows={4}
          className="w-full px-4 py-3 bg-slate-950/50 border border-white/10 rounded-xl text-white text-sm placeholder-slate-600 focus:border-emerald-500/50 focus:outline-none focus:ring-1 focus:ring-emerald-500/20 transition-all resize-none"
        />
      )}

      {isInput && !isSelect && !isRadio && !isTextarea && (
        <input
          type="text"
          value={answer}
          onChange={(e) => handleAnswer(e.target.value)}
          placeholder="Votre réponse..."
          className="w-full px-4 py-3 bg-slate-950/50 border border-white/10 rounded-xl text-white text-sm placeholder-slate-600 focus:border-emerald-500/50 focus:outline-none focus:ring-1 focus:ring-emerald-500/20 transition-all"
        />
      )}
    </div>
  );
});

const StepContact = memo(({ formData, onChange }) => (
  <div className="space-y-5">
    <div className="text-center mb-4">
      <div className="w-16 h-16 mx-auto bg-gradient-to-br from-emerald-500/20 to-indigo-500/20 rounded-2xl flex items-center justify-center mb-4 border border-emerald-500/20">
        <MessageCircle size={28} className="text-emerald-400" />
      </div>
      <h3 className="text-xl font-black text-white mb-2" style={{ fontFamily: "'Outfit', sans-serif" }}>
        Dernière étape !
      </h3>
      <p className="text-slate-400 text-sm">Comment souhaitez-vous être contacté ?</p>
    </div>

    <div className="p-4 bg-gradient-to-r from-emerald-500/10 to-teal-500/10 border border-emerald-500/20 rounded-2xl">
      <div className="flex items-start gap-3">
        <div className="p-2 bg-emerald-500/10 rounded-xl">
          <MessageCircle size={20} className="text-emerald-400" />
        </div>
        <div>
          <p className="text-sm font-bold text-emerald-300 mb-1">📱 Recommandé : WhatsApp</p>
          <p className="text-xs text-slate-400">
            Nos experts vous répondent plus rapidement sur WhatsApp. Vous pourrez échanger des documents 
            et garder une trace de tous vos échanges.
          </p>
        </div>
      </div>
    </div>

    <div className="grid grid-cols-1 gap-3">
      <button
        type="button"
        onClick={() => onChange("contact_method", "whatsapp")}
        className={`flex items-center gap-4 p-5 rounded-2xl border-2 transition-all ${
          formData.contact_method === "whatsapp"
            ? "bg-emerald-500/10 border-emerald-500 text-emerald-300"
            : "bg-slate-950/30 border-white/5 text-slate-400 hover:border-white/20"
        }`}
      >
        <div className={`p-3 rounded-xl ${formData.contact_method === "whatsapp" ? "bg-emerald-500/20" : "bg-slate-800"}`}>
          <MessageCircle size={24} className={formData.contact_method === "whatsapp" ? "text-emerald-400" : "text-slate-500"} />
        </div>
        <div className="flex-1 text-left">
          <div className="font-bold text-sm mb-0.5">WhatsApp</div>
          <div className="text-xs text-slate-500">Réponse rapide • Échange de documents • Messagerie</div>
        </div>
        {formData.contact_method === "whatsapp" && (
          <div className="w-6 h-6 rounded-full bg-emerald-500 flex items-center justify-center">
            <Check size={14} className="text-white" />
          </div>
        )}
      </button>

      <button
        type="button"
        onClick={() => onChange("contact_method", "telephone")}
        className={`flex items-center gap-4 p-5 rounded-2xl border-2 transition-all ${
          formData.contact_method === "telephone"
            ? "bg-indigo-500/10 border-indigo-500 text-indigo-300"
            : "bg-slate-950/30 border-white/5 text-slate-400 hover:border-white/20"
        }`}
      >
        <div className={`p-3 rounded-xl ${formData.contact_method === "telephone" ? "bg-indigo-500/20" : "bg-slate-800"}`}>
          <Phone size={24} className={formData.contact_method === "telephone" ? "text-indigo-400" : "text-slate-500"} />
        </div>
        <div className="flex-1 text-left">
          <div className="font-bold text-sm mb-0.5">Téléphone</div>
          <div className="text-xs text-slate-500">Appel direct par un expert</div>
        </div>
        {formData.contact_method === "telephone" && (
          <div className="w-6 h-6 rounded-full bg-indigo-500 flex items-center justify-center">
            <Check size={14} className="text-white" />
          </div>
        )}
      </button>
    </div>

    <div className="p-4 bg-slate-950/50 border border-white/5 rounded-2xl">
      <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-3 flex items-center gap-2">
        <CheckCircle2 size={12} className="text-emerald-400" />
        Vos informations sont déjà sauvegardées
      </div>
      <div className="text-xs text-slate-400">
        Toutes vos réponses sont enregistrées. En cliquant sur "Confirmer", un message WhatsApp sera automatiquement envoyé à notre équipe avec le récapitulatif complet.
      </div>
    </div>
  </div>
));

const StepConfirmation = memo(({ submitted, formData }) => (
  <div className="text-center py-4">
    {submitted ? (
      <>
        <div className="w-20 h-20 mx-auto bg-emerald-500/10 rounded-full flex items-center justify-center mb-6 border-2 border-emerald-500/30">
          <CheckCircle2 size={40} className="text-emerald-400" />
        </div>
        <h3 className="text-2xl font-black text-white mb-3" style={{ fontFamily: "'Outfit', sans-serif" }}>
          Félicitations ! 🎉
        </h3>
        <p className="text-slate-400 text-sm mb-6 leading-relaxed max-w-sm mx-auto">
          Merci <span className="text-emerald-400 font-bold">{formData.prenom}</span> ! 
          Votre demande a bien été enregistrée. Un expert va vous contacter très prochainement sur{" "}
          <span className="text-emerald-400 font-bold">
            {formData.contact_method === "whatsapp" ? "WhatsApp" : "téléphone"}
          </span>.
        </p>
        <div className="inline-flex items-center gap-2 px-5 py-3 bg-emerald-500/10 border border-emerald-500/20 rounded-xl text-emerald-300 text-xs font-bold">
          <MessageCircle size={14} />
          Réponse garantie sous 24h ouvrées
        </div>
      </>
    ) : (
      <>
        <div className="w-20 h-20 mx-auto bg-indigo-500/10 rounded-full flex items-center justify-center mb-6 border-2 border-indigo-500/30">
          <Loader2 size={40} className="text-indigo-400 animate-spin" />
        </div>
        <h3 className="text-xl font-black text-white mb-3" style={{ fontFamily: "'Outfit', sans-serif" }}>
          Envoi en cours...
        </h3>
        <p className="text-slate-400 text-sm">Préparation du message WhatsApp pour notre équipe...</p>
      </>
    )}
  </div>
));

/* ─── COMPOSANT PRINCIPAL ─── */

export default function LeadCaptureModal({ onClose }) {
  const [currentSubStep, setCurrentSubStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");
  const [questions, setQuestions] = useState([]);
  const [leadId, setLeadId] = useState(null);
  const [whatsappNumber, setWhatsappNumber] = useState("212600000000");
  const [formData, setFormData] = useState({
    nom: "",
    prenom: "",
    telephone: "",
    email: "",
    trading_experience: "",
    experience_description: "",
    domaine_travail: "",
    contact_method: "whatsapp",
    responses: {},
  });

  useEffect(() => {
    const loadInitialData = async () => {
      try {
        // Charger les questions
        const qRes = await api.get("/crm/questions");
        if (qRes.success && Array.isArray(qRes.data)) {
          setQuestions(qRes.data);
        }
        // Charger le numéro WhatsApp
        const waRes = await api.get("/crm/whatsapp-number");
        if (waRes.success && waRes.data?.whatsapp_number) {
          setWhatsappNumber(waRes.data.whatsapp_number);
        }
      } catch (err) {
        console.error("❌ Erreur chargement données:", err);
      }
    };
    loadInitialData();
  }, []);

  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = "auto"; };
  }, []);

  const step2Questions = questions.filter(q => q.step === 2);
  const totalSubSteps = 1 + step2Questions.length + 1 + 1;

  const handleChange = useCallback((field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setError("");
  }, []);

  const handleAnswer = useCallback((questionKey, value) => {
    setFormData(prev => ({
      ...prev,
      responses: { ...prev.responses, [questionKey]: value },
    }));
    setError("");
  }, []);

  const validateStep1 = () => {
    const { nom, prenom, telephone, email } = formData;
    if (!nom.trim()) return "Veuillez entrer votre nom";
    if (!prenom.trim()) return "Veuillez entrer votre prénom";
    if (!telephone.trim()) return "Veuillez entrer votre numéro de téléphone";
    if (!email.trim()) return "Veuillez entrer votre email";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return "Email invalide";
    if (!/^[+]?[\d\s\-()]{7,20}$/.test(telephone)) return "Numéro de téléphone invalide";
    return null;
  };

  const saveLead = async () => {
    setLoading(true);
    setError("");
    try {
      const payload = {
        nom: formData.nom,
        prenom: formData.prenom,
        telephone: formData.telephone,
        email: formData.email,
      };
      const res = await api.post("/crm/leads", payload);
      const leadIdFromApi = res.data?.lead_id || res.data?.id;
      if (res.success && leadIdFromApi) {
        setLeadId(leadIdFromApi);
        return leadIdFromApi;
      } else {
        throw new Error(res.message || "Erreur lors de l'enregistrement");
      }
    } catch (err) {
      console.error("❌ Save lead error:", err);
      setError(err.message || "Erreur de connexion au serveur");
      return null;
    } finally {
      setLoading(false);
    }
  };

  const saveAnswer = async (questionKey, value) => {
    if (!leadId) return false;
    try {
      await api.post(`/crm/leads/${leadId}/answers`, {
        question_key: questionKey,
        answer: value,
      });
      return true;
    } catch (err) {
      console.error("❌ Save answer error:", err);
      return false;
    }
  };

  const handleSubmitWithWhatsApp = async () => {
    setLoading(true);
    setError("");

    if (leadId) {
      await saveAnswer("contact_method", formData.contact_method);
    }

    // Construire le message WhatsApp
    const msg = `🔔 *Nouveau Lead - Ennnovatrade CRM*%0A%0A` +
      `👤 *Contact:* ${formData.prenom} ${formData.nom}%0A` +
      `📞 *Téléphone:* ${formData.telephone}%0A` +
      `📧 *Email:* ${formData.email}%0A` +
      `📊 *Expérience Trading:* ${formData.trading_experience || 'Non renseigné'}%0A` +
      (formData.experience_description ? `📝 *Description:* ${formData.experience_description}%0A` : '') +
      `💼 *Domaine:* ${formData.domaine_travail || 'Non renseigné'}%0A` +
      `📱 *Contact souhaité:* ${formData.contact_method === 'whatsapp' ? 'WhatsApp ✅' : 'Téléphone'}%0A%0A` +
      `⏰ *Date:* ${new Date().toLocaleString('fr-FR')}`;

    // Ouvrir WhatsApp avec le message pré-rempli
    // Utiliser le numéro WhatsApp configuré dans l'admin
    const cleanNumber = whatsappNumber.replace(/[^0-9]/g, '');
    const waUrl = `https://wa.me/${cleanNumber}?text=${msg}`;
    window.open(waUrl, '_blank');

    setSubmitted(true);
    setLoading(false);
  };

  const goToNextQuestion = async () => {
    const qIndex = currentSubStep - 1;
    if (qIndex >= 0 && qIndex < step2Questions.length && leadId) {
      const q = step2Questions[qIndex];
      const answer = formData.responses[q.question_key] || formData[q.question_key];
      if (answer) {
        await saveAnswer(q.question_key, answer);
      }
    }
    setError("");
    setCurrentSubStep(prev => Math.min(prev + 1, totalSubSteps - 1));
  };

  const handlePrimaryAction = async () => {
    if (currentSubStep === 0) {
      const err = validateStep1();
      if (err) { setError(err); return; }
      const id = await saveLead();
      if (id) {
        setCurrentSubStep(prev => Math.min(prev + 1, totalSubSteps - 1));
      }
    } else if (currentSubStep > 0 && currentSubStep <= step2Questions.length) {
      await goToNextQuestion();
    } else if (currentSubStep === step2Questions.length + 1) {
      await handleSubmitWithWhatsApp();
    }
  };

  const stepLabel = () => {
    if (currentSubStep === 0) return "Vos coordonnées";
    if (currentSubStep > 0 && currentSubStep <= step2Questions.length) return `Question ${currentSubStep}/${step2Questions.length}`;
    if (currentSubStep === step2Questions.length + 1) return "Méthode de contact";
    return "Confirmation";
  };

  const progressPercent = ((currentSubStep) / (totalSubSteps - 1)) * 100;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={onClose} />

      <div className="relative w-full max-w-lg bg-slate-900 border border-white/10 rounded-3xl shadow-2xl shadow-black/50 overflow-hidden animate-in zoom-in-95 duration-200">
        {!loading && (
          <button
            onClick={onClose}
            className="absolute top-4 right-4 z-10 w-8 h-8 rounded-full bg-white/5 border border-white/10 flex items-center justify-center text-slate-400 hover:text-white hover:bg-white/10 transition-all"
          >
            <X size={16} />
          </button>
        )}

        <div className="p-6">
          <div className="mb-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-[10px] font-bold uppercase tracking-widest text-emerald-400">{stepLabel()}</span>
              <span className="text-[10px] font-bold text-slate-500">
                {currentSubStep === 0 ? "Étape 1" : 
                 currentSubStep <= step2Questions.length ? `Question ${currentSubStep}/${step2Questions.length}` :
                 currentSubStep === step2Questions.length + 1 ? "Dernière étape" : "Finalisation"}
              </span>
            </div>
            <div className="w-full h-1.5 bg-slate-800 rounded-full overflow-hidden">
              <div 
                className="h-full bg-gradient-to-r from-emerald-500 to-indigo-500 rounded-full transition-all duration-500 ease-out"
                style={{ width: `${progressPercent}%` }}
              />
            </div>
          </div>

          {error && (
            <div className="mb-4 p-3 bg-rose-500/10 border border-rose-500/20 rounded-xl flex items-center gap-2 text-rose-400 text-xs font-medium">
              <AlertCircle size={14} />
              {error}
            </div>
          )}

          <div className="min-h-[280px] flex flex-col justify-center">
            {currentSubStep === 0 && (
              <StepInfos formData={formData} onChange={handleChange} />
            )}
            {currentSubStep > 0 && currentSubStep <= step2Questions.length && (
              <SingleQuestion 
                question={step2Questions[currentSubStep - 1]} 
                formData={formData}
                onAnswer={handleAnswer}
              />
            )}
            {currentSubStep === step2Questions.length + 1 && (
              <StepContact formData={formData} onChange={handleChange} />
            )}
            {currentSubStep >= step2Questions.length + 2 && (
              <StepConfirmation submitted={submitted} formData={formData} />
            )}
          </div>

          {currentSubStep < step2Questions.length + 2 && !loading && (
            <div className="flex justify-between mt-6 pt-4 border-t border-white/5">
              <div>
                {currentSubStep > 0 ? (
                  <button
                    onClick={() => { setError(""); setCurrentSubStep(prev => Math.max(prev - 1, 0)); }}
                    className="px-5 py-3 rounded-xl text-sm font-bold text-slate-400 hover:text-white bg-white/5 hover:bg-white/10 border border-white/5 transition-all flex items-center gap-2"
                  >
                    <ChevronLeft size={16} />
                    Retour
                  </button>
                ) : <div />}
              </div>
              
              <button
                onClick={handlePrimaryAction}
                disabled={loading}
                className="px-8 py-3 rounded-xl text-sm font-bold text-white bg-emerald-500 hover:bg-emerald-400 transition-all flex items-center gap-2 disabled:opacity-50 shadow-lg shadow-emerald-500/20 active:scale-95"
              >
                {currentSubStep === 0 ? (
                  <>
                    Enregistrer et continuer
                    <ChevronRight size={16} />
                  </>
                ) : currentSubStep <= step2Questions.length ? (
                  <>
                    Suivant
                    <ChevronRight size={16} />
                  </>
                ) : (
                  <>
                    <Send size={16} />
                    Confirmer et Envoyer
                  </>
                )}
              </button>
            </div>
          )}

          {loading && (
            <div className="mt-6 pt-4 border-t border-white/5 flex justify-center">
              <button
                disabled
                className="px-8 py-3 rounded-xl text-sm font-bold text-white bg-emerald-500/50 flex items-center gap-2"
              >
                <Loader2 size={16} className="animate-spin" />
                {currentSubStep === 0 ? "Enregistrement..." : "Envoi en cours..."}
              </button>
            </div>
          )}

          {submitted && (
            <div className="mt-6 pt-4 border-t border-white/5">
              <button
                onClick={onClose}
                className="w-full py-3 rounded-xl text-sm font-bold text-white bg-emerald-500 hover:bg-emerald-400 transition-all active:scale-95"
              >
                Fermer
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
